import React, { useEffect, useMemo, useState } from 'react';
import { createHelpArticle, updateHelpArticle, uploadHelpVideo } from '../helpApi';

/**
 * Shown when a recorded tour finishes: preview the video, then save it to the
 * help topic (so everyone sees it), download it, or discard it.
 */
function TourRecordingDialog({ blob, topic, onClose }) {
  const url = useMemo(() => URL.createObjectURL(blob), [blob]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const canSave = Boolean(topic && (topic.help_id || topic.guide_id));

  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      const file = new File([blob], 'tutorial.webm', { type: 'video/webm' });
      const video = await uploadHelpVideo(file, setProgress);
      if (topic.help_id) {
        await updateHelpArticle(topic.help_id, { video });
      } else {
        await createHelpArticle({
          question: topic.title,
          answer: topic.answer,
          category: topic.category || 'General',
          tags: topic.tags || [],
          guide_id: topic.guide_id,
          video,
        });
      }
      setMessage('Saved. Everyone who opens this help topic now sees the video.');
      window.dispatchEvent(new CustomEvent('help-topics:changed'));
    } catch (err) {
      setMessage(err?.response?.data?.detail || 'Could not save the video.');
    } finally {
      setSaving(false);
    }
  };

  const fileName = `${(topic?.title || 'tutorial').replace(/[^\w -]+/g, '').trim().replace(/\s+/g, '-').toLowerCase()}.webm`;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 3100, background: 'rgba(15, 23, 42, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
      }}
      data-testid="tour-recording-dialog"
    >
      <div style={{ background: 'white', borderRadius: '8px', padding: '18px', width: '100%', maxWidth: '720px', maxHeight: '92vh', overflowY: 'auto' }}>
        <h2 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 10px' }}>Tutorial video recorded</h2>
        <video src={url} controls style={{ width: '100%', background: '#000', aspectRatio: '16 / 9' }} />
        <p style={{ fontSize: '12px', color: '#6B7280', margin: '8px 0 0' }}>
          Check the recording before saving — it shows whatever was on your screen, including your own data.
        </p>
        {message && <p style={{ fontSize: '12px', color: message.startsWith('Saved') ? '#047857' : '#DC2626', margin: '8px 0 0' }}>{message}</p>}
        {saving && <p style={{ fontSize: '12px', color: '#6B7280', margin: '8px 0 0' }}>Uploading… {progress}%</p>}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px', flexWrap: 'wrap' }}>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            {message.startsWith('Saved') ? 'Close' : 'Discard'}
          </button>
          <a className="btn-secondary" href={url} download={fileName}>Download</a>
          {canSave && !message.startsWith('Saved') && (
            <button type="button" className="btn-primary" onClick={save} disabled={saving}>
              Save to “{topic.title}”
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TourRecordingDialog;
